import React from "react";
import {
  ApiType,
  ErrorSource,
  HttpMethod,
  HttpStatusCode,
  IPosConfig,
  IPosMethods,
  IPosState,
  PosApiEventListener,
  RequestHandler,
  RequestHandlerWithSessionId,
  ResponseType,
  UseLinklyPosProps,
  UseLinklyPosReturn,
} from "./types";
import {
  DisplayResponse,
  ErrorResponse,
  PairingRequest,
  PairingResponse,
  ReceiptResponse,
  ReprintReceiptRequest,
  ReprintReceiptResponse,
  ResultRequest,
  SendKeyRequest,
  StatusResponse,
} from "./models";

import log, { LogSeverity } from "./logger";
import {
  ArgumentOutOfRangeException,
  InvalidOperationException,
} from "./errors";
import { retrieveLinklyDataAsync, storeLinklyDataAsync } from "./storage";
import {
  PAIRING_ENDPOINT,
  PAIR_SECRET_STORAGE_KEY,
  REPRINT_RECEIPT_ENDPOINT,
  RESULT_ENDPOINT,
  SEND_KEY_ENDPOINT,
  STATUS_ENDPOINT,
  TOKEN_EXPIRY_STORAGE_KEY,
  TOKEN_REQUEST_ENDPOINT,
  TOKEN_STORAGE_KEY,
  TRANSACTION_ENDPOINT,
} from "./constants";

import { v4 as uuidv4 } from "uuid";
import { ApiResponse, AuthToken } from "./common";
import { JsonApiResponse } from "./common/json-api-response";
import { TokenRequest } from "./models/authentication/token-request";
import { TokenResponse } from "./models/authentication/token-response";
import { PosApiRequest } from "./models/pos-api-request";
import { TransactionRequest } from "./models/transaction-request";
import { BaseRequest } from "./models/base-request";
import { booleanToNumberString, logFetch, replacePlaceholders } from "./utils";
import { TransactionResponse } from "./models/transaction/transaction-response";
import { BaseResponse } from "./models/base-response";
import { PosApiResponse } from "./models/pos-api-response";
import { StatusRequest } from "./models/status/status-request";
import { TransactionStatusRequest } from "./models/transaction/transaction-status-request";

export function useLinklyPos({
  config: iConfig,
}: UseLinklyPosProps): UseLinklyPosReturn {
  const [config] = React.useState<IPosConfig>(iConfig);

  const [posState, updatePosState] = React.useState<IPosState>({
    isInitialized: false,
    pairSecret: undefined,
    token: undefined,
    tokenExpiry: undefined,
  });

  const authToken = React.useRef<AuthToken | undefined>(undefined);

  React.useEffect(() => {
    log(`Initializing Linkly POS`, LogSeverity.Info);
    initilizeFromStorage().then(() => {
      updatePosState((state) => ({
        ...state,
        isInitialized: true,
      }));
    });
  }, []);

  React.useEffect(() => {
    (async () => {
      if (true === posState.isInitialized) {
        await storeLinklyDataAsync(
          PAIR_SECRET_STORAGE_KEY,
          posState.pairSecret
        );
        await storeLinklyDataAsync(TOKEN_STORAGE_KEY, posState.token);
        await storeLinklyDataAsync(
          TOKEN_EXPIRY_STORAGE_KEY,
          posState.tokenExpiry
        );
      }
    })();
  }, [posState]);

  async function initilizeFromStorage() {
    log(`Initializing from local storage`, LogSeverity.Info);

    const pairSecret = await retrieveLinklyDataAsync(PAIR_SECRET_STORAGE_KEY);
    const token = await retrieveLinklyDataAsync(TOKEN_STORAGE_KEY);
    const tokenExpiry = await retrieveLinklyDataAsync(TOKEN_EXPIRY_STORAGE_KEY);

    return updatePosState((state) => ({
      ...state,
      pairSecret: pairSecret,
      token: token,
      tokenExpiry: tokenExpiry,
    }));
  }

  async function setEventListener(listener: PosApiEventListener) {
    log(`${setEventListener.name}: Setting event listener`, undefined, {
      listener,
    });
    config.eventListener = listener;
  }

  async function sendPairingRequest(request: PairingRequest) {
    await _executeCommon(_handlePairingRequest, request);
  }

  async function sendTransactionRequest(
    request: TransactionRequest
  ): Promise<string> {
    log(
      `${sendTransactionRequest.name}: Starting Transaction`,
      undefined,
      request
    );
    validatePairing();
    _setPosVendorDetails(request);
    return await _executeCommon(_handleTransactionRequest, request);
  }

  /*
  function sendResultRequest(request: ResultRequest) {
    log(`${sendResultRequest.name}: Starting`, LogSeverity.Info);
    validatePairing();
    _executeCommon(_handleResultRequest, request);
  }
*/

  function validatePairing() {
    if (!posState.pairSecret) {
      log(
        `${validatePairing.name}: Invoked without pairing`,
        LogSeverity.Error
      );
      throw new InvalidOperationException("Pairing is required");
    }
  }

  function setPairSecret(pairSecret?: string | null) {
    log(`Setting pair secret`, undefined, { pairSecret });
    if (pairSecret === posState.pairSecret) {
      log(`Pair secret is the same, ignoring`, LogSeverity.Info);
      return;
    }

    updatePosState((state) => ({
      ...state,
      token: undefined,
      tokenExpiry: undefined,
      pairSecret: pairSecret,
    }));
  }

  async function sendKeyRequest(request: SendKeyRequest) {
    log(`${sendKeyRequest.name}: Starting`, LogSeverity.Info);
    validatePairing();
    _setPosVendorDetails(request);
    await _executeCommonWithoutSessionId(_handleSendKeyRequest, request);
  }

  async function sendStatusRequest(request: StatusRequest) {
    log(`${sendStatusRequest.name}: Starting`, LogSeverity.Info);
    validatePairing();
    _setPosVendorDetails(request);
    return _executeCommon(_handleStatusRequest, request);
  }

  async function sendReprintReceiptRequest(request: ReprintReceiptRequest) {
    log(`${sendReprintReceiptRequest.name}: Starting`, LogSeverity.Info);
    validatePairing();
    _setPosVendorDetails(request);
    return await _executeCommon(_handleReprintReceiptRequest, request);
  }

  async function sendTransactionStatusRequest(
    request: TransactionStatusRequest
  ) {
    log(
      `${sendTransactionStatusRequest.name}: Requesting Transaction Status: ${request.SessionId}`
    );
    return await _executeCommonWithoutSessionId(
      _handleTransactionStatusRequest,
      request
    );
  }

  async function _handleTransactionStatusRequest(
    request: TransactionStatusRequest
  ) {
    var requestUri = replacePlaceholders(TRANSACTION_ENDPOINT, [
      request.SessionId ?? "",
    ]);

    const apiResponse = await _sendJsonPosApiRequest<TransactionResponse>(
      HttpMethod.GET,
      requestUri,
      request
    );

    const [isSuccess, maybeResponse] = apiResponse.tryGetSuccessResponse();

    log(
      `${_handleTransactionStatusRequest.name}: Status response (statusCode ${
        apiResponse.statusCode
      }, success ${isSuccess}): ${JSON.stringify(maybeResponse)}`,
      LogSeverity.Info
    );

    if (apiResponse.statusCode == HttpStatusCode.ACCEPTED) {
      await config.eventListener.onError(
        request,
        new ErrorResponse(
          ErrorSource.API,
          "Request Accepted. Please try again later.",
          apiResponse.statusCode
        ),
        request.SessionId
      );
      return;
    }

    if (!isSuccess || !maybeResponse) {
      await config.eventListener.onError(
        request,
        new ErrorResponse(
          ErrorSource.API,
          "Error completing request",
          apiResponse.statusCode
        ),
        request.SessionId
      );
      return;
    }

    await config.eventListener.onTransactionStatusRequestComplete(
      request.SessionId,
      request,
      // Make sure the class is instantiated
      new TransactionResponse(maybeResponse)
    );
  }

  async function _handleReprintReceiptRequest(
    request: ReprintReceiptRequest,
    sessionId: string
  ) {
    return await _handleCommonPosRequest<
      ReprintReceiptRequest,
      ReprintReceiptResponse
    >(
      config.eventListener.onReceiptReprintComplete,
      request,
      REPRINT_RECEIPT_ENDPOINT,
      sessionId
    );
  }

  // Internal methods for below

  /**
   * Common logic for executing requests
   */

  async function _executeCommon<T extends BaseRequest>(
    requestHandler: RequestHandlerWithSessionId<T>,
    request: T
  ): Promise<string> {
    const sessionId = uuidv4();

    (async () => {
      try {
        await requestHandler(request, sessionId);
      } catch (err) {
        log(`Error executing request`, LogSeverity.Error, { err });
      }
    })();

    return sessionId;
  }

  async function _executeCommonWithoutSessionId<T extends BaseRequest>(
    requestHandler: RequestHandler<T>,
    request: T
  ) {
    try {
      await requestHandler(request);
    } catch (err) {
      log(`Error executing request`, LogSeverity.Error, { err });
    }
  }

  async function _handleStatusRequest(
    request: StatusRequest,
    sessionId: string
  ) {
    return await _handleCommonPosRequest<StatusRequest, StatusResponse>(
      config.eventListener.onStatusCompleted,
      request,
      STATUS_ENDPOINT,
      sessionId
    );
  }
  async function _handleSendKeyRequest(request: SendKeyRequest) {
    var requestUri = replacePlaceholders(SEND_KEY_ENDPOINT, [
      request.SessionId ?? "",
    ]);

    await _sendPosApiRequest(HttpMethod.POST, requestUri, request);
  }

  async function _handleTransactionRequest(
    request: TransactionRequest,
    sessionId: string
  ) {
    return await _handleCommonPosRequest<
      TransactionRequest,
      TransactionResponse
    >(
      config.eventListener.onTransactionComplete,
      request,
      TRANSACTION_ENDPOINT,
      sessionId
    );
  }

  async function _handlePairingRequest(
    request: PairingRequest,
    sessionId: string
  ) {
    log(`Handling pairing request`, LogSeverity.Info, { request, sessionId });

    const response = await _sendJsonApiRequest<PairingResponse>(
      ApiType.Auth,
      HttpMethod.POST,
      PAIRING_ENDPOINT,
      request
    );

    const [isSuccess, maybeResponse] = response.tryGetSuccessResponse();

    if (isSuccess && maybeResponse) {
      log(`Request successful, updating pairing secret`, LogSeverity.Info);
      setPairSecret(maybeResponse.Secret);
      config.eventListener.onPairingComplete(request, maybeResponse);
    }
  }

  async function _sendPosApiRequest(
    httpMethod: HttpMethod,
    relativeUri: string,
    request: BaseRequest,
    sessionId?: string
  ) {
    log(
      `${_sendPosApiRequest.name}: Sending POS API request, Method ${httpMethod}, Uri: ${relativeUri}`,
      LogSeverity.Info
    );

    if (authToken.current?.isExpiringSoon() ?? true) {
      log(
        `${_sendPosApiRequest.name}: Token is expiring soon, refreshing`,
        LogSeverity.Info
      );

      var response = await _sendJsonApiRequest<TokenResponse>(
        ApiType.Auth,
        HttpMethod.POST,
        TOKEN_REQUEST_ENDPOINT,
        getTokenRequest(),
        sessionId
      );

      const [isSuccess, maybeResponse] = response.tryGetSuccessResponse();

      if (!isSuccess || !maybeResponse) {
        log(
          `${_sendPosApiRequest.name}: Authentication failed, aborting request`,
          LogSeverity.Error
        );
        throw new InvalidOperationException();
      }

      log(
        `${_sendPosApiRequest.name}: Authentication successful, updating token: ${maybeResponse.Token}`,
        LogSeverity.Info
      );

      authToken.current = new AuthToken(
        maybeResponse.Token,
        new Date(Date.now() + maybeResponse.ExpirySeconds * 1000)
      );
    } else {
      log(
        `${_sendPosApiRequest.name}: Token not expiring soon, authentication not required`,
        LogSeverity.Info
      );
    }

    var posApiResponse = await _sendApiRequest(
      ApiType.Pos,
      httpMethod,
      relativeUri,
      request,
      sessionId
    );

    if (posApiResponse.httpStatusCode == HttpStatusCode.Unauthorized) {
      log(
        `${_sendPosApiRequest.name}: Clearing auth token due to failed authentication`,
        LogSeverity.Info
      );
      authToken.current = undefined;
    }

    return posApiResponse;
  }

  async function _sendJsonApiRequest<T extends BaseResponse>(
    apiType: ApiType,
    httpMethod: HttpMethod,
    relativeUri: string,
    request: BaseRequest,
    sessionId?: string
  ) {
    const response = await _sendApiRequest(
      apiType,
      httpMethod,
      relativeUri,
      request,
      sessionId
    );

    return new JsonApiResponse<T>(response);
  }

  async function _sendJsonPosApiRequest<T extends BaseResponse>(
    httpMethod: HttpMethod,
    relativeUri: string,
    request: BaseRequest,
    sessionId?: string
  ) {
    const response = await _sendPosApiRequest(
      httpMethod,
      relativeUri,
      request,
      sessionId
    );

    return new JsonApiResponse<T>(response);
  }

  async function _handleCommonPosRequest<
    T extends PosApiRequest,
    J extends BaseResponse
  >(
    listener: (
      sessionId: string,
      request: T,
      response: J
    ) => Promise<void> | void,
    request: T,
    relativeUri: string,
    sessionId: string
  ) {
    log(
      `${_handleCommonPosRequest.name}: Performing request ${request.constructor.name}. Session ID: ${sessionId}`,
      LogSeverity.Info
    );

    const requestUri = replacePlaceholders(relativeUri, [sessionId]);

    const result = await _sendPosApiRequest(
      HttpMethod.POST,
      requestUri,
      request,
      sessionId
    );

    if (false == result.success) {
      log(`${_handleCommonPosRequest.name}: Request failed`, LogSeverity.Error);
      return;
    } else {
      log(
        `${_handleCommonPosRequest.name}: Request completed, fetching events`,
        LogSeverity.Info
      );
    }

    const maxDelay = 3 * 60000; // Maximum delay (60 seconds)
    const minInitialDelay = 500; // Minimum initial delay (0.5 second)
    const maxInitialDelay = 1500; // Maximum initial delay (1.5 seconds)
    let randomDelay = Math.floor(
      Math.random() * (maxInitialDelay - minInitialDelay + 1) + minInitialDelay
    ); // Random initial delay
    let delay = randomDelay;
    const startTime = Date.now();

    while (Date.now() - startTime < maxDelay) {
      log(
        `${_handleCommonPosRequest.name}: Checking for new events`,
        LogSeverity.Info
      );

      const responses = await _sendResultRequest(
        new ResultRequest({ sessionId: sessionId }),
        sessionId,
        false
      );

      if (responses.length >= 1) {
        delay = randomDelay;
      }

      for (const response of responses) {
        switch (response.constructor) {
          case DisplayResponse: {
            config.eventListener.onDisplay(
              sessionId,
              request,
              response as DisplayResponse
            );
            break;
          }
          case ReceiptResponse: {
            config.eventListener.onReceipt(
              sessionId,
              request,
              response as ReceiptResponse
            );
            break;
          }
          default: {
            listener(sessionId, request, response as any);
            return;
          }
        }
      }

      // Increase the delay exponentially
      delay *= 2;

      // Ensure the delay doesn't exceed the maximum
      if (delay > maxDelay) {
        delay = maxDelay;
      }

      // Wait for the specified delay using a promise
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    log(
      `${_handleCommonPosRequest.name}: elapsed waiting for ${listener.name}`,
      LogSeverity.Info
    );

    config.eventListener.onError(
      request,
      new ErrorResponse(ErrorSource.Internal, "Timeout waiting for response"),
      sessionId
    );
  }

  async function _handleResultRequest(request: ResultRequest) {
    const responses = await _sendResultRequest(
      request,
      request.sessionId,
      true
    );
  }

  async function _sendResultRequest(
    request: ResultRequest,
    sessionId: string,
    all: boolean
  ) {
    log(
      `${_sendResultRequest.name}: Getting POS Api events for Session ID: ${sessionId}, All: ${all}`,
      LogSeverity.Info
    );

    const requestUri = replacePlaceholders(RESULT_ENDPOINT, [
      sessionId,
      booleanToNumberString(all),
    ]);

    const apiResponse = await _sendJsonPosApiRequest<PosApiResponse[]>(
      HttpMethod.GET,
      requestUri,
      request,
      sessionId
    );

    if (apiResponse.statusCode == HttpStatusCode.HttpStatusTooEarly) {
      log(
        `${_sendResultRequest.name}: Too early, no events yet`,
        LogSeverity.Info
      );
      return [];
    }

    if (
      [HttpStatusCode.Timeout].includes(apiResponse.statusCode) ||
      (apiResponse.statusCode >= 500 && apiResponse.statusCode <= 599)
    ) {
      log(
        `${_sendResultRequest.name}: Timeout or Server Error, try again.`,
        LogSeverity.Info
      );
      return [];
    }

    const [isSuccess, maybeResponse] = apiResponse.tryGetSuccessResponse();

    if (!isSuccess || !maybeResponse) {
      log(
        `${_sendResultRequest.name}: Result request unsuccessful`,
        LogSeverity.Error
      );
      throw new InvalidOperationException();
    }

    log(
      `${_sendResultRequest.name}: Result request successful`,
      LogSeverity.Info
    );

    log(
      `${_sendResultRequest.name}: Raw responses: ${JSON.stringify(
        maybeResponse
      )}`,
      LogSeverity.Info
    );

    const responses = [];

    for (const response of maybeResponse) {
      switch (response.ResponseType.toLowerCase()) {
        case ResponseType.Transaction.toLowerCase(): {
          responses.push(new TransactionResponse(response));
          break;
        }
        case ResponseType.Display.toLowerCase(): {
          responses.push(new DisplayResponse(response));
          break;
        }
        case ResponseType.Receipt.toLowerCase(): {
          responses.push(new ReceiptResponse(response));
          break;
        }
        case ResponseType.ReprintReceipt.toLowerCase(): {
          responses.push(new ReprintReceiptResponse(response));
          break;
        }
        case ResponseType.Status.toLowerCase(): {
          responses.push(new StatusResponse(response));
          break;
        }
        default: {
          throw new ArgumentOutOfRangeException(
            `Unexpected value of  ResponseType: ${response.ResponseType}`
          );
        }
      }
    }

    return responses;
  }

  async function _sendApiRequest<T>(
    apiType: ApiType,
    httpMethod: HttpMethod,
    relativeUri: string,
    request: BaseRequest,
    sessionId?: string
  ): Promise<Promise<ApiResponse>> {
    var requestUri =
      apiType == ApiType.Auth
        ? `${config.apiServiceEndPoints.AuthApiBaseUri}${relativeUri}`
        : `${config.apiServiceEndPoints.posApiBaseUri}${relativeUri}`;

    try {
      const content = request ? request.serialize() : undefined;

      const httpRequest: any = {
        method: httpMethod,
        headers: {
          "Content-Type": "application/json",
        },
      };

      if (httpMethod === HttpMethod.POST && content) {
        httpRequest.body = JSON.stringify(content);
      }

      if (apiType == ApiType.Pos) {
        httpRequest.headers[
          "Authorization"
        ] = `Bearer ${authToken.current?.token}`;
      }

      const response = await logFetch(requestUri, httpRequest);

      if (!response.ok) {
        log(
          `${_sendApiRequest.name}: Error sending request to ${requestUri}`,
          LogSeverity.Error,
          response
        );

        if (response.status != HttpStatusCode.HttpStatusTooEarly) {
          config.eventListener.onError(
            request,
            new ErrorResponse(
              ErrorSource.API,
              "Error completing request",
              response.status
            ),
            sessionId
          );
        }
      } else {
        log(
          `${_sendApiRequest.name}: HTTP status code: ${response.status}`,
          LogSeverity.Info,
          response
        );
      }

      return new ApiResponse(
        response.ok,
        response.status,
        await response.text()
      );
    } catch (err) {
      log(`${_sendApiRequest.name}: Request unsuccessful`, LogSeverity.Error);
      config.eventListener.onError(
        request,
        new ErrorResponse(
          ErrorSource.Internal,
          "Error completing request",
          undefined
        ),
        sessionId
      );
      throw err;
    }
  }

  function _setPosVendorDetails(request: PosApiRequest) {
    request.PosId = config.vendorDetails.posId;
    request.PosName = config.vendorDetails.name;
    request.PosVersion = config.vendorDetails.version;
  }

  function getTokenRequest() {
    return new TokenRequest({
      PosId: config.vendorDetails.posId,
      PosVendorId: config.vendorDetails.posVendorId,
      PosVersion: config.vendorDetails.version,
      PosName: config.vendorDetails.name,
      Secret: posState.pairSecret ?? "",
    });
  }

  const methods: IPosMethods = React.useMemo(
    () => ({
      sendTransactionRequest,
      setPairSecret,
      sendPairingRequest,
      sendKeyRequest,
      setEventListener,
      sendStatusRequest,
      sendReprintReceiptRequest,
      sendTransactionStatusRequest,
      pairSecret: posState.pairSecret,
    }),
    [
      sendTransactionRequest,
      setPairSecret,
      sendPairingRequest,
      sendKeyRequest,
      setEventListener,
      sendStatusRequest,
      sendReprintReceiptRequest,
      sendTransactionStatusRequest,
      posState.pairSecret,
    ]
  );

  return {
    state: posState,
    methods,
  };
}
